import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CheckCircle2, ArrowLeft, Wrench, Send, Loader2, Sparkles,
  Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { usePageMeta } from '../hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// Quick Fix services with pricing
const QUICK_FIX_SERVICES = [
  { id: 'business-card', name: 'Business Card Design', price: 'from $49' },
  { id: 'google-business', name: 'Google Business Setup', price: 'from $99' },
  { id: 'social-cover', name: 'Social Media Cover', price: 'from $39' },
  { id: 'flyer', name: 'Flyer / Door Hanger', price: 'from $59' },
  { id: 'yard-sign', name: 'Yard Sign Design', price: 'from $69' },
  { id: 'logo', name: 'Simple Logo Design', price: 'from $149' },
  { id: 'website', name: 'One-Page Website', price: 'from $199' },
  { id: 'social-templates', name: 'Social Post Templates', price: 'from $79' },
  { id: 'car-magnet', name: 'Car Magnet Design', price: 'from $59' },
  { id: 'other', name: 'Something Else (describe below)', price: 'Custom quote' }
];

const businessTypes = [
  'Construction / General Contractor',
  'Lawn Care / Landscaping',
  'Plumbing',
  'HVAC',
  'Electrical',
  'Roofing',
  'Painting',
  'Cleaning Services',
  'Handyman',
  'Other Home Services',
  'Food Truck / Mobile Food',
  'Restaurant / Cafe',
  'Salon / Barbershop',
  'Auto Services',
  'Other'
];

// Form Field Component with animation
const FormField = ({ label, required, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Label className="text-gray-700 font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </motion.div>
);

const MiniServicesRequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  
  usePageMeta({
    title: 'Request a Quick Fix | BM-Assist.co',
    description: 'Get a single marketing item done fast. Business cards, Google setup, flyers, yard signs, and more for local businesses.',
    ogTitle: 'Request a Quick Fix | BM-Assist.co',
    ogDescription: 'Need just one thing? Get professional marketing materials without buying a full package.'
  });

  const [formData, setFormData] = useState({
    form_type: 'Quick Fix Request',
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    business_type: '',
    selected_services: [],
    request_category: 'quick_fix',
    main_goal: '',
    timeline: '',
    current_links: '',
    notes: '',
    source: 'quick-fixes-page'
  });

  // Pre-select service from URL param
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      const decodedService = decodeURIComponent(serviceParam);
      // Find matching service
      const matchedService = QUICK_FIX_SERVICES.find(s => 
        s.name.toLowerCase() === decodedService.toLowerCase() ||
        decodedService.toLowerCase().includes(s.name.toLowerCase().split(' ')[0])
      );
      if (matchedService) {
        setFormData(prev => ({
          ...prev,
          selected_services: [matchedService.id]
        }));
      }
    }
  }, [searchParams]);

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selected_services: prev.selected_services.includes(serviceId)
        ? prev.selected_services.filter(s => s !== serviceId)
        : [...prev.selected_services, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.selected_services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    
    setSubmitting(true);

    try {
      // Convert selected service IDs to names for storage
      const selectedServiceNames = formData.selected_services.map(id => 
        QUICK_FIX_SERVICES.find(s => s.id === id)?.name || id
      );
      
      const payload = {
        ...formData,
        selected_services: selectedServiceNames,
        submitted_at: new Date().toISOString()
      };

      await axios.post(`${API}/project-requests`, payload);
      toast.success('Quick Fix request submitted!');
      navigate('/submitted-project');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Request submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = formData.selected_services.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
              BM-Assist.co
            </Link>
            <Link to="/quick-fixes">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Quick Fixes
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decoration */}
        <motion.div
          className="absolute top-20 right-0 w-96 h-96 bg-amber-100 rounded-full filter blur-3xl opacity-40 -z-10"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-72 h-72 bg-blue-100 rounded-full filter blur-3xl opacity-40 -z-10"
          animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Wrench className="w-4 h-4" />
              Quick Fix Request
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              <span className="text-gray-900">Need just </span>
              <span className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                one thing?
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
            >
              Pick what you need, tell us about your business, and we'll get it done fast.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4 text-sm"
            >
              {[
                { icon: Zap, text: 'Fast turnaround' },
                { icon: Clock, text: '24-48 hour delivery' },
                { icon: CheckCircle2, text: 'No monthly fees' }
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm"
                >
                  <item.icon className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Service Selection */}
              <FormField label="What do you need?" required delay={0}>
                <p className="text-sm text-gray-500 mb-3">Select all that apply</p>
                <div className="grid gap-2">
                  {QUICK_FIX_SERVICES.map((service, index) => (
                    <motion.label
                      key={service.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                      whileHover={{ scale: 1.01 }}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.selected_services.includes(service.id)
                          ? 'border-[#1E3A8A] bg-[#1E3A8A]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                          formData.selected_services.includes(service.id)
                            ? 'border-[#1E3A8A] bg-[#1E3A8A]'
                            : 'border-gray-300'
                        }`}>
                          {formData.selected_services.includes(service.id) && (
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{service.name}</span>
                      </div>
                      <span className="text-sm text-[#1E3A8A] font-semibold">{service.price}</span>
                      <input
                        type="checkbox"
                        checked={formData.selected_services.includes(service.id)}
                        onChange={() => toggleService(service.id)}
                        className="sr-only"
                      />
                    </motion.label>
                  ))}
                </div>
                {selectedCount > 0 && (
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-3 text-sm text-green-600 font-medium flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {selectedCount} service{selectedCount > 1 ? 's' : ''} selected
                  </motion.p>
                )}
              </FormField>

              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Your name" required delay={0.1}>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Smith"
                    required
                    className="rounded-lg"
                  />
                </FormField>
                
                <FormField label="Email" required delay={0.15}>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    className="rounded-lg"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Phone" delay={0.2}>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="rounded-lg"
                  />
                </FormField>
                
                <FormField label="Business name" required delay={0.25}>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Smith's Landscaping"
                    required
                    className="rounded-lg"
                  />
                </FormField>
              </div>

              {/* Business Type */}
              <FormField label="What type of business?" required delay={0.3}>
                <Select
                  value={formData.business_type}
                  onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {/* Project Details */}
              <FormField label="Tell us more about what you need" delay={0.35}>
                <Textarea
                  value={formData.main_goal}
                  onChange={(e) => setFormData({ ...formData, main_goal: e.target.value })}
                  placeholder="e.g., I need business cards for a networking event next week. My brand colors are blue and white..."
                  rows={3}
                  className="rounded-lg"
                />
              </FormField>

              {/* Timeline */}
              <FormField label="When do you need it?" delay={0.4}>
                <Select
                  value={formData.timeline}
                  onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">ASAP (rush fee may apply)</SelectItem>
                    <SelectItem value="this-week">This week</SelectItem>
                    <SelectItem value="1-2-weeks">1-2 weeks</SelectItem>
                    <SelectItem value="flexible">Flexible / No rush</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              {/* Links */}
              <FormField label="Any existing branding or examples to share?" delay={0.45}>
                <Textarea
                  value={formData.current_links}
                  onChange={(e) => setFormData({ ...formData, current_links: e.target.value })}
                  placeholder="Links to your website, social media, or examples you like..."
                  rows={2}
                  className="rounded-lg"
                />
              </FormField>

              {/* Additional Notes */}
              <FormField label="Anything else we should know?" delay={0.5}>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Special requirements, brand colors, fonts, etc..."
                  rows={2}
                  className="rounded-lg"
                />
              </FormField>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 }}
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    disabled={submitting || selectedCount === 0}
                    className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-6 text-lg rounded-xl shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Quick Fix Request
                      </>
                    )}
                  </Button>
                </motion.div>
                <p className="text-center text-sm text-gray-500 mt-4">
                  We'll review your request and send a quote within 24 hours.
                </p>
              </motion.div>
            </form>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                BM-Assist.co
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} BM-Assist.co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MiniServicesRequestPage;
